/**
 * Sites page — single page with a list and an in-page detail view.
 *
 * List of sites (tenant/mine). Clicking a row opens an in-page detail with a
 * "Machines" tab showing machines attached to that site. Create/edit via drawer.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
  FilterableDataTable,
  PageContainer,
  type RowAction,
} from '../../components/shared';
import { SiteControllerApi, MachineControllerApi } from '../../api-generated/apis';
import type { Site, Machine } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';
import SiteDrawerForm from './SiteDrawerForm';

const siteApi = new SiteControllerApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);

interface SiteRow {
  id: string;
  name: string;
  customerName: string;
  location: string;
  code: string;
}

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
  { field: 'customerName', headerName: 'Customer', flex: 1, minWidth: 160 },
  { field: 'location', headerName: 'Location', flex: 0.8, minWidth: 130 },
  { field: 'code', headerName: 'Code', flex: 0.4, minWidth: 80 },
];

const SitesPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSite, setSelectedSite] = useState<SiteRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editSiteId, setEditSiteId] = useState<string | undefined>(undefined);
  const [detailTab, setDetailTab] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const sitesQuery = useQuery<Site[]>({
    queryKey: ['sites', 'list'],
    queryFn: () => siteApi.getAllSites(),
  });

  const rows: SiteRow[] = useMemo(
    () =>
      (sitesQuery.data ?? []).map((s: Site) => ({
        id: s.id ?? '',
        name: s.name ?? '',
        customerName: s.customerName ?? '',
        location: s.location ?? '',
        code: s.code ?? '',
      })),
    [sitesQuery.data],
  );

  // Machines for the selected site (client-side filter by siteId)
  const machinesQuery = useQuery<Machine[]>({
    queryKey: ['machines', 'all-for-sites'],
    queryFn: () => machineApi.getAllMachines(),
    enabled: !!selectedSite,
    staleTime: 60_000,
  });

  const siteMachines = useMemo(
    () => (machinesQuery.data ?? []).filter((m: any) => m.siteId === selectedSite?.id),
    [machinesQuery.data, selectedSite],
  );

  const handleAdd = useCallback(() => {
    setDrawerMode('create');
    setEditSiteId(undefined);
    setDrawerOpen(true);
  }, []);

  const rowActions: RowAction<SiteRow>[] = useMemo(
    () => [
      {
        label: 'Edit',
        icon: EditIcon,
        onClick: (row) => {
          setDrawerMode('edit');
          setEditSiteId(row.id);
          setDrawerOpen(true);
        },
      },
    ],
    [],
  );

  // ─── Detail view ───────────────────────────────────────────────────────
  if (selectedSite) {
    return (
      <PageContainer title={selectedSite.name} subtitle={selectedSite.customerName || 'Site'}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedSite(null)} sx={{ mb: 2, alignSelf: 'flex-start' }}>
          Back to Sites
        </Button>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={detailTab} onChange={(_e, v: number) => setDetailTab(v)}>
            <Tab label={`Machines (${siteMachines.length})`} />
          </Tabs>
        </Box>
        {detailTab === 0 && (
          <Box>
            {machinesQuery.isLoading && <Typography color="text.secondary">Loading…</Typography>}
            {!machinesQuery.isLoading && siteMachines.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No machines attached to this site yet.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {siteMachines.map((m: any) => (
                <Box
                  key={m.id}
                  onClick={() => navigate(`/machines/${m.id}`)}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2">{m.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.serialNumber ? `S/N ${m.serialNumber}` : ''}
                    </Typography>
                  </Box>
                  <Chip label={m.active ? 'Active' : 'Inactive'} size="small" color={m.active ? 'success' : 'default'} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PageContainer>
    );
  }

  // ─── List view ─────────────────────────────────────────────────────────
  return (
    <PageContainer title="Sites" subtitle="Manage mines / tenant sites">
      <FilterableDataTable<SiteRow>
        rows={rows}
        columns={columns}
        loading={sitesQuery.isLoading}
        totalCount={rows.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        primaryAction={{ label: 'Add Site', onClick: handleAdd }}
        onRowClick={(row) => {
          setSelectedSite(row);
          setDetailTab(0);
        }}
        actions={rowActions}
        searchPlaceholder="Search sites…"
        testIdPrefix="sites"
      />
      <SiteDrawerForm open={drawerOpen} onClose={() => setDrawerOpen(false)} mode={drawerMode} siteId={editSiteId} />
    </PageContainer>
  );
};

export default SitesPage;
